import {
	buildStorefrontHead,
	buildStorefrontHeadTags,
	matchStorefrontRoute,
	normalizeStorefrontPath,
	type StorefrontHeadModel,
} from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { applyStorefrontHeadTags } from '../lib/storefrontHead';
import { usePortalSiteStore } from '../stores/portalSiteStore';
import { useStorefrontHeadStore } from '../stores/storefrontHeadStore';

/**
 * PD-17174 — keeps the document's title, description, robots, canonical,
 * share-card and structured-data tags right for the page on screen.
 *
 * The HTML the page loaded with already carries these tags for the first
 * page: the CloudFront edge function wrote them from crm-api's
 * `api/portal/seo/head`, which builds them with the same shared builder used
 * here. So on first load this rewrites them with identical values, and after
 * that it follows the shopper's navigation, which is what a crawler that runs
 * JavaScript ends up reading.
 *
 * Mounted beside TenantGate (not inside it) so the not-configured 404 gets
 * its noindex too. Renders nothing.
 */
export function StorefrontHead(): null {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const site = usePortalSiteStore((s) => s.site);
	const isNotConfigured = usePortalSiteStore((s) => s.isNotConfigured);
	const product = useStorefrontHeadStore((s) => s.product);
	const notFoundPath = useStorefrontHeadStore((s) => s.notFoundPath);

	useEffect(() => {
		const path = normalizeStorefrontPath(pathname);
		const origin = window.location.origin;

		if (site == null) {
			// Still bootstrapping: keep the tags the HTML came with.
			if (!isNotConfigured) {
				return;
			}
			// No storefront at this address (TenantGate shows its 404 page).
			const head: StorefrontHeadModel = {
				status: 404,
				title: t('Page not found'),
				description: '',
				canonical: null,
				robots: 'noindex, nofollow',
				url: `${origin}${path}`,
				image: null,
				ogType: 'website',
				jsonLd: null,
			};
			applyStorefrontHeadTags(document, buildStorefrontHeadTags(head, ''));
			return;
		}

		const isProductPage =
			matchStorefrontRoute(path)?.route.pattern === '/product/:id';
		const pageProduct = product?.path === path ? product.value : undefined;
		const isNotFound = notFoundPath === path;
		// A product page keeps the tags it has until its product loads or turns
		// out to be missing, so a rendering crawler never reads a generic title
		// in between.
		if (isProductPage && pageProduct == null && !isNotFound) {
			return;
		}

		const head = buildStorefrontHead({
			path,
			site: {
				name: site.displayName,
				// The storefront's primary host, not necessarily this one: every
				// host it answers on names the same canonical address.
				origin: site.canonicalOrigin ?? origin,
				// The light-surface wordmark, as crm-api picks for the edge: link
				// previews show the image on a light card.
				logoUrl: site.logoUrlLight ?? site.logoUrl,
				// An older crm-api omits the field; treat that as not indexable.
				indexingState: site.searchEngineIndexing ?? 'noindex',
			},
			product: isNotFound ? null : pageProduct,
			notFound: isNotFound,
			translate: t,
		});
		applyStorefrontHeadTags(
			document,
			buildStorefrontHeadTags(head, t(site.displayName)),
		);
	}, [pathname, site, isNotConfigured, product, notFoundPath, t]);

	return null;
}
